import debug from 'debug';

import { AgentRole } from 'constants/agent-role';
import { DebugLogsNamespace } from 'constants/debug-logs-namespace';
import { anyToBoolean } from 'helpers/boolean';
import { getConfig } from 'helpers/config';
import { Product } from 'interfaces/products';
import { propertiesClient } from 'services/connectivity/configuration-api/properties/client';
import { accountsClient } from 'services/connectivity/global-accounts-api/accounts/client';
import { ME_ACCOUNT_ID } from 'services/connectivity/global-accounts-api/accounts/constants';
import { organizationsClient } from 'services/connectivity/global-accounts-api/organizations/client';
import { MY_ORGANIZATION_ID } from 'services/connectivity/global-accounts-api/organizations/constants';
import { type ProductTracking } from 'services/connectivity/global-accounts-api/organizations/types';
import { IntegrationLicensePropertiesNames } from 'store/entities/integration-license-properties/interfaces';

const log = debug(DebugLogsNamespace.AppStartup);

/**
 * Ceiling on how long application start will wait for the gate. The three requests measured
 * 379ms, 385ms and 650ms, and a lost race costs the attribution permanently — the gate closes
 * once `signup_event_tracked` flips. The GTM bootstrap in `views/index.ejs` falls back at 10s,
 * so this stays well clear of it.
 */
const GATE_TIMEOUT_MS = 2000;

/** Widened, because Global Accounts types `role` as a plain string. */
const OWNER_ROLE: string = AgentRole.Owner;

type GateData = {
  isOwner: boolean;
  hasSignupEventBeenSent: boolean;
  tracking: ProductTracking | null;
};

/**
 * Resolves to `null` when `promise` has not settled within `timeoutMs`. The slow promise is
 * abandoned rather than aborted — its request still completes, its result is discarded.
 */
async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T | null> {
  let timeoutId = 0;

  const timeout = new Promise<null>((resolve) => {
    timeoutId = window.setTimeout(() => resolve(null), timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timeoutId));
}

/**
 * Fetches everything the gate needs in a single concurrent round. Resolves to `null` if any
 * request fails, so a transient failure retries on the next load instead of being cached.
 *
 * The organization payload is fetched alongside the two gate checks rather than behind them.
 * That wastes one GET whenever the gate turns out to be closed, in exchange for one round trip
 * of added startup latency instead of two.
 */
async function fetchGateData(): Promise<GateData | null> {
  const { accountsClientId } = getConfig();

  const [account, licenseProperties, organization] = await Promise.all([
    accountsClient.get(ME_ACCOUNT_ID),
    propertiesClient.listLicense({ namespace: accountsClientId }),
    organizationsClient.get(MY_ORGANIZATION_ID),
  ]);

  if (account.error !== null || licenseProperties.error !== null || organization.error !== null) {
    return null;
  }

  /**
   * Account roles already carry `product` and `role`, so ownership needs no separate roles
   * lookup.
   *
   * Matched on `role` alone, deliberately. The organization owner is a *primary* role under the
   * `Accounts` product (`product: 'Accounts', role: 'owner', type: 'primary'`) — LiveChat's own
   * roles only go up to `viceowner`. Also pairing this with `product === 'LiveChat'` would
   * never match anyone. This mirrors `filterRoles` in `services/api/accounts/deserializer.ts`,
   * which likewise treats `role === 'owner'` as product-agnostic.
   */
  const isOwner = account.result.roles.some((role) => role.role === OWNER_ROLE);

  /**
   * `signup_event_tracked` lives under the accounts client id namespace — the one the saga
   * writes and `getHasSignupEventBeenSent` reads. Not `PlatformNamespace.CORE`.
   */
  const accountsProperties = licenseProperties.result[accountsClientId];
  const hasSignupEventBeenSent = anyToBoolean(
    accountsProperties?.[IntegrationLicensePropertiesNames.SignupEventTracked],
  );

  return {
    isOwner,
    hasSignupEventBeenSent,
    tracking: organization.result.tracking?.[Product.LiveChat] ?? null,
  };
}

/**
 * Computes the search params for `url` with the organization's tracking key/values filled in
 * for whichever keys are missing.
 *
 * Never overrides a param already present, regardless of its value and including an empty
 * string — the backend snapshot is captured once at product-activation time and can be stale
 * relative to whatever is live in the current session's URL.
 *
 * Returns `null` when there is nothing to add.
 */
export function buildTrackingSearchParams(url: URL, tracking: ProductTracking): URLSearchParams | null {
  const params = new URLSearchParams(url.search);
  let changed = false;

  Object.entries(tracking).forEach(([key, value]) => {
    if (params.has(key)) {
      return;
    }

    params.set(key, value);
    changed = true;
  });

  return changed ? params : null;
}

/**
 * Rewrites the address bar with the organization's stored signup attribution, for owners whose
 * Signup conversion event has not fired yet.
 *
 * An agent who signed up on `livechat.com` lands here without the campaign params that led to
 * the signup, so neither store-metrics nor the GTM tags on this page can see them. Global
 * Accounts keeps that snapshot on the organization; this puts it back in the URL.
 *
 * Must run before `initStoreMetrics()`: the package reads `document.location.href` on its
 * inbound pass, and the GTM bootstrap in `views/index.ejs` waits on the `store-metrics:ready`
 * event that pass dispatches. Both therefore see the enriched URL on the first `page_view`.
 *
 * The gate is re-evaluated on every full page load. There is deliberately no persisted marker,
 * so the requests above — and the delay they add before store-metrics and GTM boot — are paid
 * each time `start()` runs, including for organizations that will never be eligible.
 *
 * Never throws and never rejects — attribution enrichment must not be able to block start().
 */
export async function injectOrganizationTrackingParams(): Promise<void> {
  try {
    const gateData = await withTimeout(fetchGateData(), GATE_TIMEOUT_MS);

    if (!gateData) {
      log('Organization tracking params: gate unresolved');

      return;
    }

    const { isOwner, hasSignupEventBeenSent, tracking } = gateData;

    if (!isOwner || hasSignupEventBeenSent) {
      log('Organization tracking params: gate closed', { isOwner, hasSignupEventBeenSent });

      return;
    }

    /**
     * Organizations activated before Global Accounts started snapshotting `tracking` have
     * nothing to inject. That is also the population `trackUserSignup` skips via its cutoff, so
     * `signup_event_tracked` never flips true for them and this branch is reached on every load.
     */
    if (!tracking) {
      log('Organization tracking params: no tracking snapshot');

      return;
    }

    const url = new URL(window.location.href);
    const params = buildTrackingSearchParams(url, tracking);

    if (!params) {
      log('Organization tracking params: url already carries every tracked param');

      return;
    }

    url.search = params.toString();
    window.history.replaceState(window.history.state, '', url.href);

    log('Organization tracking params: injected', { search: url.search });
  } catch {
    // Enrichment is best-effort. Failures must never keep the application from starting.
  }
}
